﻿
/**
 * This class is designed to control access to a resource in an environment where multiple asynchronous operations
 * may be competing for that resource. See more comprehensive comments below the class for usage and details.
 */
export class AsyncSemaphore {
  constructor() {
    this.isLocked = false;
    this.waiting = [];
  }

  lockAsync() {
    const unlock = () => {
      let nextResolve;
      if (this.waiting.length > 0) {
        nextResolve = this.waiting.pop(0);
        nextResolve(unlock);
      } else {
        this.isLocked = false;
      }
    };

    if (this.isLocked) {
      return new Promise(resolve => {
        this.waiting.push(resolve);
      });
    } else {
      this.isLocked = true;
      return new Promise(resolve => {
        resolve(unlock);
      });
    }
  }
}

/* The AsyncSemaphore class was created based on a chat on ChatGPT on 2/1/2024.
   See https://chat.openai.com/share/1b1313cd-87cd-4221-a869-05871a1a9252. The chat contains some
   other topics as well, just scroll down to the part which shows the code example for a Lock class.
   The class is renamed to AsyncSemaphore further down.

   Here's an example of how to use AsyncSemaphore. Assume you have a class Telemetry which accesses
   sessionStorage in its getTelemetryIdAsync asynchronous method. The method will get the Id from
   sessionStorage if present there. Otherwise it needs to make a call to an external web api to get it,
   hence the async nature of it.

   Let's assume that you have more than one instance of the Telemetry class in use on the same page.
   That means there's a possibility of re-entrancy of the getTelemetryIdAsync method since instance 2 on
   the page might call getTelemetryIdAsync() while instance 1 is awaiting the web api call to complete).
   The method get's the Id from the web api and saves it into sessionStorage. But, instance 2 needs to
   wait for instance 1 to complete to avoid a race condition where instance 2 also gets an Id from the
   web api and overwrites instance 1's id in the sessionStorage.

   To solve this challenge, we create a SessionStorageManager class which wraps the access to sessionStorage
   and protects it from re-antrant access and race conditions. In the SessionStorageManager class we create
   an instance of AsyncSemaphore and add a lockAccessAsync() async method which is used in the
   Telemetry.getTelemetryIdAsync() method. Here's the code:

    class SessionStorageManager {
      constructor(storageKey) {
        this.storageKey = storageKey;
        this._semaphore = new AsyncSemaphore();
      }

      async lockAccessAsync() {
        return await this._semaphore.lockAsync();
      }

      getItem() {
        return sessionStorage.getItem(this.storageKey);
      }

      setItem(item) {
        sessionStorage.setItem(this.storageKey, item);
      }
    }

    class Telemetry {
      async getTelemetryIdAsync() {
        const unlock = await sessionStorageMgr.lockAccessAsync(); // This call will block if another instance has already called it
        try {
          let id = sessionStorageMgr.getItem();
          if (!id) {
            const id = await getIdFromWebApi(); // Access the external web api:
            sessionStorageMgr.setItem(id);
          }
          return id;
        } finally {
          unlock(); // This will release the lock and let other instances proceed.
        }
      }
    }
*/

